import { z } from 'zod';

/**
 * SHAREABLE INVITATION LINKS AND THE OWNER'S APPROVAL OF EACH REQUEST (owner
 * brief "Shareable Project Invitation Links & Approval Workflow", 2026-11-02).
 *
 * The vocabulary the API, the worker and the SPA share. The rules that USE it
 * live on the server (`backend/src/modules/join/`): the SPA renders these
 * answers and decides nothing — "the SPA hints, the server gates".
 *
 * THREE THINGS THAT ARE TRUE EVERYWHERE THIS FILE IS READ:
 *
 * - A link never grants access. Holding it lets a signed-in person ASK; every
 *   request is decided one by one, by the production's owner alone.
 * - The token travels in the URL FRAGMENT (`/join#<token>`), never in a path or a
 *   query. A browser does not send a fragment to any server — not in a request
 *   line, not in a Referer — so nginx, Traefik and every log in between never see
 *   it. The SPA moves it into this tab's sessionStorage before the router starts
 *   and strips it from the address bar; API calls carry it in a JSON body.
 * - An invalid, expired, revoked or replaced link, or one whose production is
 *   gone, is ONE answer: `JOIN_LINK_INVALID`, "This invitation link is no longer
 *   valid". Telling them apart would tell a stranger which tokens once existed.
 */

/** Same lifetime as an email invitation (`TeamService`, INVITE_TTL_DAYS). */
export const JOIN_LINK_TTL_DAYS = 14;

/** 32 random bytes, base64url, no padding: 43 characters. */
export const JOIN_TOKEN_BYTES = 32;
export const JOIN_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** Where a link opens in the SPA. The token follows as the fragment. */
export const JOIN_PATH = '/join';

export function joinUrl(appOrigin: string, token: string): string {
  return `${appOrigin.replace(/\/+$/, '')}${JOIN_PATH}#${token}`;
}

/** The token out of a `location.hash`, or null when it is not one. */
export function tokenFromFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return JOIN_TOKEN_PATTERN.test(raw) ? raw : null;
}

// ─── Codes ────────────────────────────────────────────────────────────────────

export const JOIN_LINK_INVALID = 'JOIN_LINK_INVALID';
export const JOIN_LINK_INVALID_MESSAGE = 'This invitation link is no longer valid';
/** Generate refused because a live link exists and `replace` was not asked for. */
export const JOIN_LINK_EXISTS = 'JOIN_LINK_EXISTS';
export const JOIN_LINK_NOT_FOUND = 'JOIN_LINK_NOT_FOUND';
export const JOIN_REQUEST_NOT_FOUND = 'JOIN_REQUEST_NOT_FOUND';
/** A second review of a decided request. */
export const JOIN_REQUEST_ALREADY_DECIDED = 'JOIN_REQUEST_ALREADY_DECIDED';
/** Only the production's owner, while an active member, decides a request. */
export const JOIN_APPROVER_ONLY = 'JOIN_APPROVER_ONLY';
export const JOIN_REQUEST_SELF_APPROVAL = 'JOIN_REQUEST_SELF_APPROVAL';
/** The requester's account is deleted or suspended (or unknown to the platform). */
export const JOIN_REQUESTER_INELIGIBLE = 'JOIN_REQUESTER_INELIGIBLE';
/** The platform could not say whether the requester may join: nothing changed. */
export const JOIN_ELIGIBILITY_UNKNOWN = 'JOIN_ELIGIBILITY_UNKNOWN';
/** Too many requests through one link in an hour. */
export const JOIN_REQUESTS_RATE_LIMITED = 'JOIN_REQUESTS_RATE_LIMITED';
/** "Enter production" on a request that cannot be entered (not approved, or no longer). */
export const JOIN_REQUEST_NOT_ENTERABLE = 'JOIN_REQUEST_NOT_ENTERABLE';

// ─── Bodies ───────────────────────────────────────────────────────────────────

export const JoinTokenBody = z.object({ token: z.string().regex(JOIN_TOKEN_PATTERN) });
export type JoinTokenBody = z.infer<typeof JoinTokenBody>;

export const GenerateJoinLinkBody = z.object({ replace: z.boolean().optional() }).default({});
export type GenerateJoinLinkBody = z.infer<typeof GenerateJoinLinkBody>;

// ─── The link, as the team sees it ────────────────────────────────────────────

/** Stored: ACTIVE | REVOKED | REPLACED. Expired is read from the clock. */
export const JoinLinkStatus = z.enum(['ACTIVE', 'REVOKED', 'REPLACED']);
export type JoinLinkStatus = z.infer<typeof JoinLinkStatus>;

/** What the team page shows: the one link that is live, or that last was. */
export type JoinLinkState = 'ACTIVE' | 'EXPIRED';

export interface JoinLinkSummary {
  readonly id: string;
  readonly state: JoinLinkState;
  readonly createdAt: string;
  readonly expiresAt: string;
}

/** The current link, never its token: the token is stored only as a hash. */
export interface JoinLinkView {
  readonly link: JoinLinkSummary | null;
}

/** The ONE answer that carries the token — to the person who generated it, once. */
export interface JoinLinkCreated {
  readonly link: JoinLinkSummary;
  readonly url: string;
  /** True when a live link was replaced by this one. */
  readonly replaced: boolean;
}

export function joinLinkState(expiresAt: Date, now: Date): JoinLinkState {
  return expiresAt.getTime() <= now.getTime() ? 'EXPIRED' : 'ACTIVE';
}

// ─── Requests ─────────────────────────────────────────────────────────────────

export const JoinRequestStatus = z.enum([
  'PENDING_APPROVAL',
  /** Approved; the account still needs the Production workspace. No membership yet. */
  'APPROVED_AWAITING_ACTIVATION',
  'APPROVED',
  'REJECTED',
]);
export type JoinRequestStatus = z.infer<typeof JoinRequestStatus>;

export const JOIN_REQUEST_STATUS_LABELS: Readonly<Record<JoinRequestStatus, string>> = Object.freeze({
  PENDING_APPROVAL: 'Pending approval',
  APPROVED_AWAITING_ACTIVATION: 'Approved · awaiting account activation',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
});

/** Where a request came from. One origin today; named so a list can say it. */
export const JOIN_REQUEST_ORIGIN_LABEL = 'Invitation link';

/** One request as the team's list shows it. People facts come from the platform. */
export interface JoinRequestRow {
  readonly id: string;
  readonly userId: number;
  /** Null when the platform's directory could not be read just now. */
  readonly displayName: string | null;
  readonly email: string | null;
  readonly emailVerified: boolean | null;
  /** ACTIVE | SUSPENDED | DELETED as the platform says it; null when unknown. */
  readonly accountState: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | null;
  /**
   * Whether ScoutAround has enabled the Production workspace for the account.
   * False means an approval waits for activation; null when unknown.
   */
  readonly workspaceEnabled: boolean | null;
  readonly status: JoinRequestStatus;
  readonly origin: 'INVITATION_LINK';
  readonly requestedAt: string;
  readonly decidedAt: string | null;
  readonly authorizationRole: string | null;
  readonly activatedAt: string | null;
}

export interface JoinRequestList {
  readonly requests: readonly JoinRequestRow[];
  /** Whether THIS person may approve or reject, and the sentence when not. */
  readonly viewer: { readonly canDecide: boolean; readonly refusal: string | null };
  /** False when names and addresses could not be read: the rows still list, by id. */
  readonly peopleAvailable: boolean;
}

export interface JoinDecisionResult {
  readonly status: JoinRequestStatus;
  /** Approved and the person was already on the team: nothing about them changed. */
  readonly alreadyMember: boolean;
}

/** The sentence the owner's refusal carries, everywhere it is shown. */
export const APPROVER_ONLY_SENTENCE =
  "Only the production's owner can approve or reject requests to join.";

// ─── The requester's view ─────────────────────────────────────────────────────

/**
 * Where the person holding a link stands with this production.
 *
 * - CAN_REQUEST — the link works and they have not asked.
 * - PENDING_APPROVAL — they asked; the owner has not decided.
 * - AWAITING_ACTIVATION — approved; ScoutAround has not enabled the Production
 *   workspace for their account yet, so nothing is open to them.
 * - READY_TO_ENTER — approved, and the account now holds the workspace: one
 *   press of Enter creates the membership.
 * - MEMBER — they are on the team.
 * - REJECTED — the owner said no to this request.
 * - ENDED — approved once, no longer on the team.
 * - INVALID — the link is no longer valid and they have nothing open here.
 */
export type JoinerState =
  | 'CAN_REQUEST'
  | 'PENDING_APPROVAL'
  | 'AWAITING_ACTIVATION'
  | 'READY_TO_ENTER'
  | 'MEMBER'
  | 'REJECTED'
  | 'ENDED'
  | 'INVALID';

export function joinerState(input: {
  readonly linkUsable: boolean;
  readonly member: boolean;
  readonly request: { readonly status: JoinRequestStatus } | null;
  readonly holdsWorkspaceRole: boolean;
}): JoinerState {
  if (input.member) return 'MEMBER';

  const request = input.request;
  if (request !== null) {
    switch (request.status) {
      case 'PENDING_APPROVAL':
        return 'PENDING_APPROVAL';
      case 'APPROVED_AWAITING_ACTIVATION':
        return input.holdsWorkspaceRole ? 'READY_TO_ENTER' : 'AWAITING_ACTIVATION';
      case 'REJECTED':
        return 'REJECTED';
      case 'APPROVED':
        return 'ENDED';
    }
  }

  return input.linkUsable ? 'CAN_REQUEST' : 'INVALID';
}

/** What the landing page renders. The title only where the person may know it. */
export interface JoinerView {
  readonly state: JoinerState;
  readonly projectTitle: string | null;
  readonly projectId: string | null;
  readonly requestId: string | null;
  readonly requestedAt: string | null;
  readonly decidedAt: string | null;
}

/** The signed-out preview: the title, nothing else. */
export interface JoinLinkPreview {
  readonly projectTitle: string;
}

/** One of the caller's own requests, for `/join` with no link in hand. */
export interface MyJoinRequest {
  readonly requestId: string;
  readonly projectId: string;
  readonly projectTitle: string;
  readonly state: JoinerState;
  readonly requestedAt: string;
  readonly decidedAt: string | null;
}

export interface EnterResult {
  readonly projectId: string;
}

// ─── The Overview ─────────────────────────────────────────────────────────────

/** "3 people are waiting to join your production" — the Action Center line. */
export function waitingToJoinTitle(n: number): string {
  return n === 1
    ? '1 person is waiting to join your production'
    : `${String(n)} people are waiting to join your production`;
}

// ─── Team emails ──────────────────────────────────────────────────────────────

export const TeamNotificationKind = z.enum([
  'JOIN_REQUEST_RECEIVED',
  'JOIN_REQUEST_APPROVED',
  'JOIN_REQUEST_ACTIVATION_REQUIRED',
  'JOIN_REQUEST_REJECTED',
]);
export type TeamNotificationKind = z.infer<typeof TeamNotificationKind>;
